import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './order.css';
import ClipLoader from 'react-spinners/ClipLoader';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const ORDERS_PER_PAGE = 25;

const getAddress = (order) =>
    order.shipping_address || order.billing_address || order.customer?.default_address || {};

const getLineItems = (order) => (Array.isArray(order.line_items) ? order.line_items : []);

const getOrderTotals = (order) => {
    const items = getLineItems(order);
    const itemQty = items.reduce((sum, item) => sum + Number(item?.quantity || 0), 0);
    const fulfillableQty = items.reduce((sum, item) => sum + Number(item?.fulfillable_quantity || 0), 0);
    const refundedQty = (order.refunds || [])
        .flatMap((refund) => refund.refund_line_items || [])
        .reduce((sum, line) => sum + Number(line?.quantity || 0), 0);
    return { itemQty, fulfillableQty, refundedQty, items };
};

const OrdersTable = () => {
    const [allOrders, setAllOrders] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState('lite');
    const [search, setSearch] = useState('');
    const [financialStatus, setFinancialStatus] = useState('all');
    const [fulfillmentStatus, setFulfillmentStatus] = useState('all');
    const [onlyRefunded, setOnlyRefunded] = useState(false);
    const [minTotal, setMinTotal] = useState('');
    const [maxTotal, setMaxTotal] = useState('');

    const fetchAllOrders = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/api/dih/orders`, {
                params: { from: fromDate, to: toDate },
            });
            setAllOrders(res.data.orders || []);
            setCurrentPage(1);
        } catch (err) {
            console.error('Error fetching orders:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (fromDate && toDate) fetchAllOrders();
    }, [fromDate, toDate]);

    const filteredOrders = useMemo(() => {
        const q = search.trim().toLowerCase();
        const min = minTotal === '' ? null : Number(minTotal);
        const max = maxTotal === '' ? null : Number(maxTotal);

        return allOrders.filter((order) => {
            const address = getAddress(order);
            const totals = getOrderTotals(order);
            const totalPrice = Number(order.total_price || 0);
            const orderFulfillment = String(order.fulfillment_status || 'unfulfilled').toLowerCase();
            const orderFinancial = String(order.financial_status || '').toLowerCase();
            const haystack = [
                order.order_number,
                order.name,
                order.customer?.first_name,
                order.customer?.last_name,
                address.city,
                address.province,
                address.zip,
                ...(totals.items || []).map((item) => `${item?.name || ''} ${item?.sku || ''}`),
            ]
                .join(' ')
                .toLowerCase();

            if (q && !haystack.includes(q)) return false;
            if (financialStatus !== 'all' && orderFinancial !== financialStatus) return false;
            if (fulfillmentStatus !== 'all' && orderFulfillment !== fulfillmentStatus) return false;
            if (onlyRefunded && totals.refundedQty <= 0) return false;
            if (min !== null && !Number.isNaN(min) && totalPrice < min) return false;
            if (max !== null && !Number.isNaN(max) && totalPrice > max) return false;

            return true;
        });
    }, [allOrders, search, financialStatus, fulfillmentStatus, onlyRefunded, minTotal, maxTotal]);

    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));
    const pageOrders = useMemo(() => {
        const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
        return filteredOrders.slice(startIndex, startIndex + ORDERS_PER_PAGE);
    }, [filteredOrders, currentPage]);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(1);
    }, [totalPages, currentPage]);

    const financialStatuses = useMemo(
        () =>
            Array.from(
                new Set(allOrders.map((order) => String(order.financial_status || '').toLowerCase()).filter(Boolean))
            ),
        [allOrders]
    );

    const fulfillmentStatuses = useMemo(
        () =>
            Array.from(
                new Set(
                    allOrders
                        .map((order) => String(order.fulfillment_status || 'unfulfilled').toLowerCase())
                        .filter(Boolean)
                )
            ),
        [allOrders]
    );

    const exportToCSV = () => {
        if (!filteredOrders.length) return;
        const headers = [
            'Order ID',
            'Order Number',
            'Customer',
            'City',
            'Item Count',
            'Fulfillable Qty',
            'Refunded Qty',
            'Financial Status',
            'Fulfillment Status',
            'Order Total',
        ];

        const rows = filteredOrders.map((order) => {
            const address = getAddress(order);
            const totals = getOrderTotals(order);
            return [
                order.id,
                order.order_number || '',
                `${order.customer?.first_name || ''} ${order.customer?.last_name || ''}`.trim(),
                address.city || '',
                totals.itemQty,
                totals.fulfillableQty,
                totals.refundedQty,
                order.financial_status || '',
                order.fulfillment_status || 'unfulfilled',
                order.total_price || 0,
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `dih_shopify_orders_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const maxPageButtons = 10;
        const currentBatchStart = Math.floor((currentPage - 1) / maxPageButtons) * maxPageButtons + 1;
        const currentBatchEnd = Math.min(currentBatchStart + maxPageButtons - 1, totalPages);
        const pageNumbers = [];
        for (let i = currentBatchStart; i <= currentBatchEnd; i++) pageNumbers.push(i);

        return (
            <nav>
                <ul className="pagination justify-content-center mt-3">
                    <li className={`page-item ${currentBatchStart === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentBatchStart - 1)}
                            disabled={currentBatchStart === 1}
                        >
                            &laquo;
                        </button>
                    </li>
                    {pageNumbers.map((num) => (
                        <li key={num} className={`page-item ${num === currentPage ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(num)}>
                                {num}
                            </button>
                        </li>
                    ))}
                    <li className={`page-item ${currentBatchEnd === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentBatchEnd + 1)}
                            disabled={currentBatchEnd === totalPages}
                        >
                            &raquo;
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <div className="orderByDate container mt-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h2 className="mb-0">DIH Shopify Orders</h2>
                <div className="btn-group view-mode-switch">
                    <button
                        className={`btn btn-sm ${viewMode === 'lite' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('lite')}
                    >
                        Lite View
                    </button>
                    <button
                        className={`btn btn-sm ${viewMode === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('advanced')}
                    >
                        Advanced View
                    </button>
                </div>
            </div>

            <div className="card filter-card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-2">
                            <label className="form-label">From</label>
                            <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">To</label>
                            <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Search</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Order, customer, city, sku"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Financial</label>
                            <select
                                className="form-select"
                                value={financialStatus}
                                onChange={(e) => setFinancialStatus(e.target.value)}
                            >
                                <option value="all">All</option>
                                {financialStatuses.map((status) => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Fulfillment</label>
                            <select
                                className="form-select"
                                value={fulfillmentStatus}
                                onChange={(e) => setFulfillmentStatus(e.target.value)}
                            >
                                <option value="all">All</option>
                                {fulfillmentStatuses.map((status) => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-1 d-grid">
                            <button className="btn btn-success" onClick={exportToCSV} disabled={!filteredOrders.length}>
                                CSV
                            </button>
                        </div>
                    </div>

                    {viewMode === 'advanced' && (
                        <div className="row g-3 mt-1">
                            <div className="col-md-2">
                                <label className="form-label">Min Total</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={minTotal}
                                    onChange={(e) => setMinTotal(e.target.value)}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">Max Total</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={maxTotal}
                                    onChange={(e) => setMaxTotal(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                                <div className="form-check mt-2">
                                    <input
                                        id="onlyRefundedDIH"
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={onlyRefunded}
                                        onChange={(e) => setOnlyRefunded(e.target.checked)}
                                    />
                                    <label htmlFor="onlyRefundedDIH" className="form-check-label">
                                        Show refunded orders only
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-center my-5">
                    <ClipLoader color="#36d7b7" size={50} />
                    <p className="mt-3">Loading orders...</p>
                </div>
            ) : (
                <>
                    <div className="table-responsive">
                        {viewMode === 'lite' ? (
                            <table className="table table-striped table-hover table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Order #</th>
                                        <th>Customer</th>
                                        <th>City</th>
                                        <th>Items</th>
                                        <th>Fulfillable</th>
                                        <th>Refunded</th>
                                        <th>Financial</th>
                                        <th>Fulfillment</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageOrders.length > 0 ? (
                                        pageOrders.map((order) => {
                                            const address = getAddress(order);
                                            const totals = getOrderTotals(order);
                                            return (
                                                <tr key={order.id}>
                                                    <td className="fw-semibold">{order.order_number || '—'}</td>
                                                    <td>{order.customer?.first_name} {order.customer?.last_name}</td>
                                                    <td>{address.city || '—'}</td>
                                                    <td>{totals.itemQty}</td>
                                                    <td>{totals.fulfillableQty}</td>
                                                    <td>{totals.refundedQty}</td>
                                                    <td>{order.financial_status || '—'}</td>
                                                    <td>{order.fulfillment_status || 'unfulfilled'}</td>
                                                    <td>${order.total_price || 0}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="text-center">No orders found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        ) : (
                            <table className="table table-striped table-bordered">
                                <thead className="table-dark">
                                    <tr>
                                        <th>ID</th>
                                        <th>Order #</th>
                                        <th>Customer</th>
                                        <th>Created</th>
                                        <th>Item Name</th>
                                        <th>SKU</th>
                                        <th>Qty</th>
                                        <th>Fulfillable</th>
                                        <th>Item Status</th>
                                        <th>Total</th>
                                        <th>Financial</th>
                                        <th>Fulfillment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageOrders.length > 0 ? (
                                        pageOrders.flatMap((order) => {
                                            const items = getLineItems(order);
                                            const rows = items.length ? items : [null];
                                            return rows.map((item, idx) => (
                                                <tr key={`${order.id}-${idx}`}>
                                                    <td>{order.id}</td>
                                                    <td>{order.order_number || '—'}</td>
                                                    <td>{order.customer?.first_name} {order.customer?.last_name}</td>
                                                    <td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : '—'}</td>
                                                    <td>{item?.name || '—'}</td>
                                                    <td>{item?.sku || '—'}</td>
                                                    <td>{item?.quantity ?? '—'}</td>
                                                    <td>{item?.fulfillable_quantity ?? 0}</td>
                                                    <td>{item?.fulfillment_status || 'NA'}</td>
                                                    <td>${order.total_price || 0}</td>
                                                    <td>{order.financial_status || '—'}</td>
                                                    <td>{order.fulfillment_status || 'unfulfilled'}</td>
                                                </tr>
                                            ));
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="12" className="text-center">No orders found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {renderPagination()}

                    <div className="text-center mt-2">
                        <p className="mb-0">
                            Showing <strong>{pageOrders.length}</strong> of <strong>{filteredOrders.length}</strong> filtered orders
                            {' '}| Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

export default OrdersTable;
